import { useEffect, useState } from "react";
import "./App.css";
import brand from "./assets/brand/tokens";
import { getEnabledFeatures } from "./config/featureFlags";
import { isPlausibleEmail, normalizeEmail } from "./utils/inputValidation";
import { track } from "./utils/analytics";
import { TrialMatching } from "./features/TrialMatching";
import { SDOHScreening } from "./features/SDOHScreening";
import { FoodAsMedicine } from "./features/FoodAsMedicine";
import { DecentralizedTrials } from "./features/DecentralizedTrials";
import { EquityDashboard } from "./features/EquityDashboard";

const LINKEDIN_URL = "https://www.linkedin.com/company/ground-truth-health/";
const CONTACT_FORM_ID = "contact-form";

const services = [
  {
    icon: "roadmap",
    title: "AI Strategy & Adoption Roadmaps",
    text: "Readiness assessments and implementation roadmaps built for your organization's actual capacity, not a vendor's ideal.",
    tags: ["Technology assessment", "Use case prioritization", "Implementation planning"],
  },
  {
    icon: "shield",
    title: "Responsible AI Governance",
    text: "Frameworks and policies for ethical, compliant, auditable AI deployment aligned with NIST AI RMF and FDA guidance.",
    tags: ["Ethics frameworks", "Bias monitoring", "Regulatory compliance"],
  },
  {
    icon: "map",
    title: "Health Equity & Access Analytics",
    text: "Geospatial and population analysis that shows where your care model reaches — and where it doesn't.",
    tags: ["Disparities mapping", "SDOH integration", "Site selection"],
  },
  {
    icon: "network",
    title: "Clinical Research Access",
    text: "Infrastructure connecting safety-net and community sites to the trials their patients should be in.",
    tags: ["Trial matching", "Decentralized design", "Community partnership"],
  },
];

const packages = [
  {
    duration: "4–6 weeks",
    name: "AI Readiness Assessment",
    text: "Comprehensive evaluation of your organization's AI maturity, infrastructure, and strategic readiness.",
    deliverables: [
      "Current state analysis",
      "AI maturity scorecard",
      "Gap analysis report",
      "Prioritized roadmap",
      "Executive presentation",
    ],
  },
  {
    duration: "8–12 weeks",
    name: "Responsible AI Governance Framework",
    text: "Complete governance structure for ethical, compliant, and trustworthy AI deployment.",
    popular: true,
    deliverables: [
      "Governance policies",
      "Ethics review process",
      "Risk assessment framework",
      "Monitoring protocols",
      "Training materials",
    ],
  },
  {
    duration: "3–5 weeks",
    name: "Health Equity Diagnostic",
    text: "Data-driven picture of who your care model reaches and where access breaks down.",
    deliverables: [
      "Population and geospatial analysis",
      "Access gap map",
      "SDOH screening review",
      "Prioritized intervention list",
    ],
  },
  {
    duration: "6–10 weeks",
    name: "Data Modernization Blueprint",
    text: "Strategic plan for turning legacy data systems into modern, AI-ready infrastructure.",
    deliverables: [
      "Architecture assessment",
      "Target state design",
      "Migration roadmap",
      "Vendor recommendations",
      "Implementation guide",
    ],
  },
];

const differentiators = [
  {
    icon: "layers",
    title: "Multidisciplinary Expertise",
    text: "Product strategy, AI engineering, clinical informatics, and doctoral public health training in one practice.",
  },
  {
    icon: "scales",
    title: "Equity as Method",
    text: "Equity isn't a values statement here. It's built into study design, model evaluation, and site selection.",
  },
  {
    icon: "book",
    title: "Evidence-Based Approach",
    text: "Methods grounded in peer-reviewed research, not vendor marketing or conference-stage hype.",
  },
  {
    icon: "diamond",
    title: "Boutique Attention",
    text: "You work directly with the principal. No account layer, no junior handoff.",
  },
];

const inPractice = [
  {
    category: "GOVERNANCE",
    icon: "shield",
    title: "AI Oversight That Survives an Audit.",
    text: "Governance frameworks aligned to NIST AI RMF and FDA guidance, built so clinical and compliance leadership can both sign off.",
    chips: ["Documented risk framework", "Ethics review process", "Audit-ready artifacts"],
  },
  {
    category: "ACCESS",
    icon: "map",
    title: "Finding the Patients Your Model Misses.",
    text: "Geospatial and population analysis that turns \"we serve everyone\" into a map showing exactly who isn't reached.",
    chips: ["Access gap mapping", "SDOH screening design", "Site prioritization"],
  },
  {
    category: "INFRASTRUCTURE",
    icon: "server",
    title: "Data Systems Ready for Clinical AI.",
    text: "Assessment and roadmap for moving legacy data environments toward interoperable, AI-ready architecture.",
    chips: ["Architecture assessment", "Interoperability roadmap", "Migration sequencing"],
  },
];

const aboutPillars = [
  { icon: "sparkle", title: "Clarity", text: "Cutting through complexity to deliver decisions you can defend." },
  { icon: "compass", title: "Insight", text: "Deep expertise in AI, clinical informatics, and public health." },
  { icon: "root", title: "Ground Truth", text: "Findings validated where care actually happens, not only where it's convenient to study." },
];

function App() {
  const flags = getEnabledFeatures();

  useEffect(() => {
    track("page_view");
  }, []);

  const anyFeatureMounted =
    flags.trialMatching ||
    flags.sdohScreening ||
    flags.foodAsMedicine ||
    flags.decentralizedTrials ||
    flags.equityDashboard;

  return (
    <main className="site">
      <SiteNav />
      <Hero />
      <Services />
      <Packages />
      <Differentiators />
      <InPractice />
      <About />
      <Founder />
      <Contact />
      <SiteFooter />

      {anyFeatureMounted && (
        <>
          {flags.trialMatching && <TrialMatching />}
          {flags.sdohScreening && <SDOHScreening />}
          {flags.foodAsMedicine && <FoodAsMedicine />}
          {flags.decentralizedTrials && <DecentralizedTrials />}
          {flags.equityDashboard && <EquityDashboard />}
        </>
      )}
    </main>
  );
}

function SiteNav() {
  const [open, setOpen] = useState(false);
  const links = [
    ["Services", "#services"],
    ["Packages", "#packages"],
    ["About", "#about"],
    ["Insights", "#in-practice"],
    ["Contact", "#contact"],
  ];
  return (
    <header className="nav">
      <div className="nav-inner">
        <a href="#top" className="nav-brand" aria-label={`${brand.name} home`}>
          <LogoMark />
          <span className="nav-wordmark">
            Ground Truth<span className="nav-wordmark-accent">Health.</span>
          </span>
        </a>

        <nav className={`nav-links ${open ? "is-open" : ""}`} aria-label="Primary">
          {links.map(([label, href]) => (
            <a key={href} href={href} onClick={() => setOpen(false)} className="nav-link">
              {label}
            </a>
          ))}
        </nav>

        <div className="nav-actions">
          <a className="btn btn-primary btn-glow" href="#contact">
            Book a Consultation
          </a>
          <button
            type="button"
            className="nav-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <span aria-hidden="true">{open ? "×" : "≡"}</span>
          </button>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section id="top" className="hero">
      <div className="hero-bg" aria-hidden="true">
        <div className="hero-grid" />
        <div className="hero-glow" />
      </div>
      <div className="hero-content">
        <div className="pill">
          <span className="pill-dot" />
          <span className="pill-text">Health Equity & AI Advisory</span>
        </div>
        <h1 className="hero-headline">
          <span>Clinical AI that reaches</span>
          <span className="hero-headline-accent">the patients everyone else</span>
          <span>leaves out.</span>
        </h1>
        <p className="hero-sub">
          Strategy, governance, and infrastructure for health systems serving the
          communities research forgets.
        </p>
        <div className="hero-ctas">
          <a href="#contact" className="btn btn-primary btn-glow">
            Book a Consultation <ArrowIcon />
          </a>
          <a href="#services" className="btn btn-ghost">
            Explore Services
          </a>
        </div>
        <div className="hero-scroll" aria-hidden="true">
          <span className="hero-scroll-ring">
            <span className="hero-scroll-dot" />
          </span>
        </div>
      </div>
    </section>
  );
}

function SectionHead({ eyebrow, headline, accent, sub }) {
  const headlineNode = accent
    ? (() => {
        const idx = headline.indexOf(accent);
        if (idx === -1) return headline;
        const before = headline.slice(0, idx);
        const after = headline.slice(idx + accent.length);
        return (
          <>
            {before}
            <span className="accent-gradient">{accent}</span>
            {after}
          </>
        );
      })()
    : headline;

  return (
    <header className="section-head">
      <span className="eyebrow">{eyebrow}</span>
      <h2 className="section-headline">{headlineNode}</h2>
      {sub && <p className="section-sub">{sub}</p>}
    </header>
  );
}

function Services() {
  return (
    <section id="services" className="section">
      <div className="container">
        <SectionHead
          eyebrow="SERVICES"
          headline="Comprehensive AI & Health Equity Advisory"
          accent="Health Equity"
          sub="Expert guidance across the full spectrum of healthcare technology and equitable care delivery."
        />
        <div className="grid-2">
          {services.map((s) => (
            <a key={s.title} href="#contact" className="card service-card">
              <span className="card-arrow" aria-hidden="true"><ArrowUpRight /></span>
              <span className="icon-tile"><Icon name={s.icon} /></span>
              <h3 className="card-title">{s.title}</h3>
              <p className="card-text">{s.text}</p>
              <div className="tag-row">
                {s.tags.map((t) => (
                  <span key={t} className="tag">{t}</span>
                ))}
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function Packages() {
  return (
    <section id="packages" className="section section-alt">
      <div className="container">
        <SectionHead
          eyebrow="SIGNATURE PACKAGES"
          headline="Structured Engagements, Measurable Impact"
          sub="Purpose-built consulting packages designed to deliver clarity, reduce risk, and accelerate your work."
        />
        <div className="grid-2">
          {packages.map((p) => (
            <article
              key={p.name}
              className={`card package-card ${p.popular ? "is-popular" : ""}`}
            >
              {p.popular && <span className="badge-popular">Most Popular</span>}
              <div className="package-meta">
                <ClockIcon />
                <span>{p.duration}</span>
              </div>
              <h3 className="card-title">{p.name}</h3>
              <p className="card-text">{p.text}</p>
              <ul className="deliverables">
                {p.deliverables.map((d) => (
                  <li key={d}>
                    <span className="check"><CheckIcon /></span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                className={`btn btn-block ${p.popular ? "btn-primary" : "btn-ghost"}`}
              >
                Learn More
              </a>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function Differentiators() {
  return (
    <section id="why" className="section">
      <div className="container two-col">
        <div className="two-col-left">
          <span className="eyebrow">WHY GROUND TRUTH HEALTH</span>
          <h2 className="section-headline align-left">Evidence Meets Equity</h2>
          <p className="section-sub align-left">
            Health AI is full of promises that were never tested on the populations
            they'll be used on. We bring doctoral-level research methods and real
            product experience to organizations that can't afford to get this wrong.
          </p>
          <blockquote className="pull-quote">
            "The future of healthcare demands both technological sophistication and
            ethical rigor. We help organizations achieve both."
          </blockquote>
        </div>
        <div className="two-col-right grid-2 airy">
          {differentiators.map((d) => (
            <div key={d.title} className="feature-block">
              <span className="icon-tile"><Icon name={d.icon} /></span>
              <h3 className="feature-title">{d.title}</h3>
              <p className="feature-text">{d.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function InPractice() {
  return (
    <section id="in-practice" className="section section-alt">
      <div className="container">
        <SectionHead
          eyebrow="IN PRACTICE"
          headline="What Engagements Look Like"
          sub="How this work shows up inside real healthcare organizations."
        />
        <div className="stack">
          {inPractice.map((p) => (
            <article key={p.title} className="card practice-card">
              <span className="icon-tile icon-tile-lg"><Icon name={p.icon} /></span>
              <div className="practice-body">
                <span className="practice-cat">{p.category}</span>
                <h3 className="card-title">{p.title}</h3>
                <p className="card-text">{p.text}</p>
              </div>
              <div className="practice-chips">
                {p.chips.map((c) => (
                  <span key={c} className="tag">{c}</span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section id="about" className="section">
      <div className="container container-narrow">
        <SectionHead
          eyebrow="ABOUT GROUND TRUTH HEALTH"
          headline="Strategy Grounded in Community"
          accent="Community"
          sub="Ground Truth Health helps healthcare organizations adopt AI responsibly and effectively — with a specific focus on the safety-net hospitals, community health centers, and public health agencies that serve the patients clinical research most often overlooks. We bring strategy, governance, and analytics grounded in doctoral public health research and hands-on health technology product work."
        />
        <div className="grid-3">
          {aboutPillars.map((a) => (
            <div key={a.title} className="pillar">
              <span className="icon-tile"><Icon name={a.icon} /></span>
              <h3 className="pillar-title">{a.title}</h3>
              <p className="pillar-text">{a.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Founder() {
  return (
    <section id="founder" className="section section-alt">
      <div className="container">
        <div className="founder-card">
          <div className="founder-portrait" aria-hidden="true">
            <span className="founder-monogram">gt<span className="founder-monogram-slash">/</span>h</span>
          </div>
          <div className="founder-body">
            <span className="eyebrow">PRACTICE</span>
            <h3 className="founder-name">A boutique advisory practice.</h3>
            <p className="founder-subtitle">
              Health Equity, AI Governance &amp; Digital Health Advisory
            </p>
            <p className="founder-para">
              We bring more than a decade of work at the intersection of healthcare,
              technology, and public health — spanning AI product management, clinical
              informatics research, and doctoral training in health policy. Our focus
              areas include cardiometabolic clinical trial access in federally qualified
              health centers and the geographic patterns that shape who gets offered a
              trial at all.
            </p>
            <p className="founder-para">
              We center responsible AI governance, health system digital transformation,
              and evidence-based approaches to technology adoption that hold patient
              safety and equity as design requirements rather than afterthoughts.
            </p>
            <div className="founder-links">
              <a href="#contact" className="link-inline">
                <ArrowIcon /> Get in Touch
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Contact() {
  const [form, setForm] = useState({ name: "", org: "", email: "", message: "" });
  const [emailError, setEmailError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState("idle"); // idle | success | error
  const [errorMsg, setErrorMsg] = useState("");

  function set(field, value) {
    setForm((f) => ({ ...f, [field]: value }));
    if (field === "email" && emailError) setEmailError("");
  }

  async function onSubmit(e) {
    e.preventDefault();
    const email = normalizeEmail(form.email);
    if (!isPlausibleEmail(email)) {
      setEmailError("Enter a valid email address.");
      return;
    }
    setEmailError("");
    setSubmitting(true);
    setErrorMsg("");
    try {
      const res = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          name: form.name,
          organization: form.org,
          message: form.message,
        }),
      });
      if (res.ok) {
        track("consultation_success");
        setState("success");
      } else {
        const data = await res.json().catch(() => ({}));
        track("consultation_error", { reason: data.error ?? "unknown" });
        setErrorMsg(
          data.error === "invalid_email"
            ? "Enter a valid email address."
            : "Something went wrong — please try again."
        );
        setState("error");
      }
    } catch {
      track("consultation_error", { reason: "network" });
      setErrorMsg("Network error — please try again.");
      setState("error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="contact" className="section">
      <div className="container two-col">
        <div className="two-col-left">
          <span className="eyebrow">CONTACT</span>
          <h2 className="section-headline align-left">
            Let's Build the Future <span className="accent-gradient">Together</span>
          </h2>
          <p className="section-sub align-left">
            Ready to move on AI responsibly? Schedule a consultation to talk through
            your challenges and where we can help.
          </p>
          <div className="contact-rows">
            <button
              type="button"
              className="contact-row contact-row-button"
              onClick={() => focusContactForm()}
            >
              <span className="icon-tile"><MailIcon /></span>
              <div className="contact-row-body">
                <div className="contact-row-label">Message</div>
                <div className="contact-row-value">Send a secure message</div>
              </div>
              <span className="contact-row-cta" aria-hidden="true"><ArrowIcon /></span>
            </button>
            <a href={LINKEDIN_URL} className="contact-row" target="_blank" rel="noreferrer">
              <span className="icon-tile"><LinkedInIcon /></span>
              <div>
                <div className="contact-row-label">LinkedIn</div>
                <div className="contact-row-value">Connect with Ground Truth Health</div>
              </div>
            </a>
          </div>
          <div className="expect-card">
            <span className="eyebrow">WHAT TO EXPECT</span>
            <ul className="expect-list">
              <li><ArrowIcon /> Response within one business day</li>
              <li><ArrowIcon /> 30-minute discovery call</li>
              <li><ArrowIcon /> Customized engagement proposal</li>
            </ul>
          </div>
        </div>
        <div className="two-col-right">
          {state === "success" ? (
            <div className="form-success card" role="status">
              <span className="check"><CheckIcon /></span>
              <div>
                <div className="form-success-title">Thanks — your request is in.</div>
                <div className="form-success-body">
                  We'll reply within one business day at {form.email || "the email you provided"}.
                </div>
              </div>
            </div>
          ) : (
            <form id={CONTACT_FORM_ID} className="card contact-form" onSubmit={onSubmit} noValidate>
              <label className="field">
                <span className="field-label">Name</span>
                <input
                  className="field-input"
                  type="text"
                  autoComplete="name"
                  maxLength={120}
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  required
                />
              </label>
              <label className="field">
                <span className="field-label">Organization</span>
                <input
                  className="field-input"
                  type="text"
                  autoComplete="organization"
                  maxLength={160}
                  value={form.org}
                  onChange={(e) => set("org", e.target.value)}
                />
              </label>
              <label className="field">
                <span className="field-label">Email</span>
                <input
                  className="field-input"
                  type="email"
                  autoComplete="email"
                  maxLength={254}
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  aria-invalid={emailError ? "true" : "false"}
                  aria-describedby={emailError ? "contact-email-error" : undefined}
                  required
                />
                {emailError && (
                  <span id="contact-email-error" className="field-error" role="alert">
                    {emailError}
                  </span>
                )}
              </label>
              <label className="field">
                <span className="field-label">What are you working on?</span>
                <textarea
                  className="field-input"
                  rows={5}
                  maxLength={2000}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                />
              </label>
              {state === "error" && errorMsg && (
                <div className="form-error" role="alert">{errorMsg}</div>
              )}
              <button
                type="submit"
                className="btn btn-primary btn-block btn-glow"
                disabled={submitting}
              >
                {submitting ? "Sending…" : "Request a Consultation"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function SiteFooter() {
  return (
    <footer className="footer">
      <div className="container footer-inner">
        <div className="footer-brand">
          <div className="footer-wordmark">
            Ground Truth Health<span className="footer-wordmark-dot">.</span>
          </div>
          <p className="footer-tagline">
            Health Equity, AI Governance &amp; Digital Health Advisory
          </p>
        </div>
        <nav className="footer-links" aria-label="Footer">
          <a href="#services">Services</a>
          <a href="#packages">Packages</a>
          <a href="#about">About</a>
          <a href="#contact">Contact</a>
        </nav>
        <div className="footer-icons">
          <button
            type="button"
            className="footer-icon footer-icon-btn"
            aria-label="Send a message"
            onClick={() => focusContactForm()}
          >
            <MailIcon />
          </button>
          <a
            href={LINKEDIN_URL}
            className="footer-icon"
            target="_blank"
            rel="noreferrer"
            aria-label="LinkedIn"
          >
            <LinkedInIcon />
          </a>
        </div>
      </div>
      <div className="container footer-legal">
        © 2026 Ground Truth Health LLC. All rights reserved.
      </div>
    </footer>
  );
}

function focusContactForm() {
  if (typeof window === "undefined") return;
  const section = document.getElementById("contact");
  if (section) section.scrollIntoView({ behavior: "smooth", block: "start" });
  const firstField = document.querySelector(`#${CONTACT_FORM_ID} input, #${CONTACT_FORM_ID} textarea`);
  if (firstField && typeof firstField.focus === "function") {
    setTimeout(() => firstField.focus({ preventScroll: true }), 350);
  }
}

function LogoMark() {
  return (
    <img
      src={`${process.env.PUBLIC_URL}/brand-logo.png`}
      alt={`${brand.name} logo`}
      className="brand-logo"
      width={48}
      height={38}
      decoding="async"
    />
  );
}

function Icon({ name }) {
  const p = {
    width: 26,
    height: 26,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.6,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };
  switch (name) {
    case "roadmap":
      return (
        <svg {...p}>
          <path d="M4 6h10" />
          <path d="M4 12h16" />
          <path d="M4 18h7" />
          <circle cx="16" cy="6" r="2" />
          <circle cx="20" cy="12" r="2" />
          <circle cx="13" cy="18" r="2" />
        </svg>
      );
    case "shield":
      return (
        <svg {...p}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
          <path d="m9 12 2 2 4-5" />
        </svg>
      );
    case "map":
      return (
        <svg {...p}>
          <path d="M9 3 3 5v16l6-2 6 2 6-2V3l-6 2Z" />
          <path d="M9 3v16" />
          <path d="M15 5v16" />
        </svg>
      );
    case "network":
      return (
        <svg {...p}>
          <circle cx="6" cy="6" r="2" />
          <circle cx="18" cy="6" r="2" />
          <circle cx="12" cy="18" r="2" />
          <path d="M7.6 7.4 11 15.5" />
          <path d="M16.4 7.4 13 15.5" />
          <path d="M8 6h8" />
        </svg>
      );
    case "layers":
      return (
        <svg {...p}>
          <path d="m12 3 9 5-9 5-9-5 9-5Z" />
          <path d="m3 13 9 5 9-5" />
          <path d="m3 18 9 5 9-5" />
        </svg>
      );
    case "scales":
      return (
        <svg {...p}>
          <path d="M12 3v18" />
          <path d="M6 21h12" />
          <path d="M4 8h6" />
          <path d="M14 8h6" />
          <path d="M4 8 2 14a4 4 0 0 0 8 0Z" />
          <path d="M20 8l-2 6a4 4 0 0 0 8 0Z" />
        </svg>
      );
    case "book":
      return (
        <svg {...p}>
          <path d="M4 4h10a4 4 0 0 1 4 4v12H8a4 4 0 0 1-4-4Z" />
          <path d="M4 4v12a4 4 0 0 1 4-4h10" />
        </svg>
      );
    case "diamond":
      return (
        <svg {...p}>
          <path d="m12 3 8 8-8 10-8-10Z" />
          <path d="M4 11h16" />
        </svg>
      );
    case "server":
      return (
        <svg {...p}>
          <rect x="3" y="4" width="18" height="7" rx="2" />
          <rect x="3" y="13" width="18" height="7" rx="2" />
          <path d="M7 7.5h.01" />
          <path d="M7 16.5h.01" />
        </svg>
      );
    case "sparkle":
      return (
        <svg {...p}>
          <path d="M12 3v6" />
          <path d="M12 15v6" />
          <path d="M3 12h6" />
          <path d="M15 12h6" />
          <path d="m6 6 4 4" />
          <path d="m14 14 4 4" />
        </svg>
      );
    case "compass":
      return (
        <svg {...p}>
          <circle cx="12" cy="12" r="9" />
          <path d="m14.5 9.5-2.5 5-5 2.5 2.5-5Z" />
        </svg>
      );
    case "root":
      return (
        <svg {...p}>
          <path d="M12 3v10" />
          <path d="M12 13c-3 0-6 2-6 5" />
          <path d="M12 13c3 0 6 2 6 5" />
          <path d="M12 13c0 3-2 5-4 6" />
          <path d="M12 13c0 3 2 5 4 6" />
        </svg>
      );
    default:
      return (
        <svg {...p}>
          <circle cx="12" cy="12" r="8" />
        </svg>
      );
  }
}

function ArrowIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

function ArrowUpRight() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 17 17 7" />
      <path d="M8 7h9v9" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m5 12 4 4 10-10" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="3" />
      <path d="M8 10v7" />
      <path d="M8 7v.01" />
      <path d="M12 17v-4a2 2 0 0 1 4 0v4" />
      <path d="M12 13v4" />
    </svg>
  );
}

export const landingPageSmokeTests = [
  { name: "renders hero headline", expectedText: "Clinical AI that reaches" },
  { name: "renders services grid", expectedFeatureCount: services.length },
  { name: "renders packages grid", expectedFeatureCount: packages.length },
];

export default App;
